import { Fragment } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { reviewKeyOf, submitKeysOf } from "./components/workflow/useWorkflowPerms";
import { UnifiedDashboardProvider } from "./contexts/UnifiedDashboardContext";
import Login from "./components/Login";
import ChangePassword from "./components/ChangePassword";
import LoadingSpinner from "./components/LoadingSpinner";
import Layout from "./components/shared/Layout";
import RequirePermission from "./components/shared/RequirePermission";
import DashboardSelector from "./pages/DashboardSelector";
import MyRecords from "./pages/MyRecords";
import MyEngagementDetail from "./pages/MyEngagementDetail";
import MySessionDetail from "./pages/MySessionDetail";
import MySessionQuizEditor from "./pages/MySessionQuizEditor";
import ClientHub from "./pages/ClientHub";
import ClientHubEntity from "./pages/ClientHubEntity";
import QMDashboard from "./pages/QMDashboard";
import QMEngagements from "./pages/QMEngagements";
import QMQcRecords from "./pages/QMQcRecords";
import QMEngagementDetail from "./pages/QMEngagementDetail";
import QMQcRecordDetail from "./pages/QMQcRecordDetail";
import QMQcRecordNew from "./pages/QMQcRecordNew";
import QMEngagementRecords from "./pages/QMEngagementRecords";
import QMEngagementRecordDetail from "./pages/QMEngagementRecordDetail";
import QMStaff from "./pages/QMStaff";
import QMMonthlyRecords from "./pages/QMMonthlyRecords";
import QMRegisters from "./pages/QMRegisters";
import QMAdaptiveCookie from "./pages/QMAdaptiveCookie";
import QMChargeableImport from "./pages/QMChargeableImport";
import QMTimesheetCheck from "./pages/QMTimesheetCheck";
import LDDashboard from "./pages/LDDashboard";
import LDRecords from "./pages/LDRecords";
import LDQuizzes from "./pages/LDQuizzes";
import LDReminder from "./pages/LDReminder";
import LDQuizEditor from "./pages/LDQuizEditor";
import QuizPage from "./pages/QuizPage";
import ManageData from "./pages/ManageData";
import WorkflowHub from "./pages/WorkflowHub";
import WorkflowEngReg from "./pages/WorkflowEngReg";
import WorkflowEngRegNew from "./pages/WorkflowEngRegNew";
import WorkflowEngRegDetail from "./pages/WorkflowEngRegDetail";
import WorkflowEngRegEdit from "./pages/WorkflowEngRegEdit";
import WorkflowCwcc from "./pages/WorkflowCwcc";
import WorkflowCwccNew from "./pages/WorkflowCwccNew";
import WorkflowCwccDetail from "./pages/WorkflowCwccDetail";
import WorkflowEsc from "./pages/WorkflowEsc";
import WorkflowEscNew from "./pages/WorkflowEscNew";
import WorkflowEscDetail from "./pages/WorkflowEscDetail";
import WorkflowScreening from "./pages/WorkflowScreening";
import WorkflowScreeningNew from "./pages/WorkflowScreeningNew";
import WorkflowScreeningDetail from "./pages/WorkflowScreeningDetail";
import QMScreeningRecords from "./pages/QMScreeningRecords";
import QMScreeningRecordNew from "./pages/QMScreeningRecordNew";
import QMScreeningRecordDetail from "./pages/QMScreeningRecordDetail";
import WorkflowFs from "./pages/WorkflowFs";
import WorkflowFsNew from "./pages/WorkflowFsNew";
import WorkflowFsDetail from "./pages/WorkflowFsDetail";
import WorkflowQc35 from "./pages/WorkflowQc35";
import WorkflowQc35New from "./pages/WorkflowQc35New";
import WorkflowQc35Detail from "./pages/WorkflowQc35Detail";
import WorkflowQc35a from "./pages/WorkflowQc35a";
import WorkflowQc35aNew from "./pages/WorkflowQc35aNew";
import WorkflowQc35aDetail from "./pages/WorkflowQc35aDetail";
import WorkflowEar from "./pages/WorkflowEar";
import WorkflowQca from "./pages/WorkflowQca";
import WorkflowQcaNew from "./pages/WorkflowQcaNew";
import WorkflowQcaDetail from "./pages/WorkflowQcaDetail";
import WorkflowQcad from "./pages/WorkflowQcad";
import WorkflowQcadNew from "./pages/WorkflowQcadNew";
import WorkflowQcadDetail from "./pages/WorkflowQcadDetail";
import WorkflowQcadRecord from "./pages/WorkflowQcadRecord";
import WorkflowCar from "./pages/WorkflowCar";
import WorkflowCarNew from "./pages/WorkflowCarNew";
import WorkflowCarDetail from "./pages/WorkflowCarDetail";
import WorkflowFsa from "./pages/WorkflowFsa";
import WorkflowFsaNew from "./pages/WorkflowFsaNew";
import WorkflowFsaDetail from "./pages/WorkflowFsaDetail";
import WorkflowEarNew from "./pages/WorkflowEarNew";
import WorkflowEarDetail from "./pages/WorkflowEarDetail";
import WorkflowClient from "./pages/WorkflowClient";
import WorkflowClientNew from "./pages/WorkflowClientNew";
import WorkflowClientDetail from "./pages/WorkflowClientDetail";
import WorkflowQc from "./pages/WorkflowQc";
import WorkflowQcNew from "./pages/WorkflowQcNew";
import WorkflowQcDetail from "./pages/WorkflowQcDetail";
import WorkflowCpd from "./pages/WorkflowCpd";
import WorkflowCpdNew from "./pages/WorkflowCpdNew";
import WorkflowCpdDetail from "./pages/WorkflowCpdDetail";
import WorkflowPq from "./pages/WorkflowPq";
import WorkflowPqNew from "./pages/WorkflowPqNew";
import WorkflowPqDetail from "./pages/WorkflowPqDetail";
import CheckinPage from "./pages/CheckinPage";
import FeedbackForm from "./pages/FeedbackForm";
import WorkflowAbsence from "./pages/WorkflowAbsence";
import WorkflowAbsenceNew from "./pages/WorkflowAbsenceNew";
import WorkflowAbsenceDetail from "./pages/WorkflowAbsenceDetail";
import WorkflowDispute from "./pages/WorkflowDispute";
import WorkflowDisputeNew from "./pages/WorkflowDisputeNew";
import WorkflowDisputeDetail from "./pages/WorkflowDisputeDetail";
import SettingsClients from "./pages/SettingsClients";
import SettingsLists from "./pages/SettingsLists";
import SettingsOverdue from "./pages/SettingsOverdue";
import SettingsRoles from "./pages/SettingsRoles";
import SettingsNotifications from "./pages/SettingsNotifications";
import SettingsAdmin from "./pages/SettingsAdmin";
import SettingsAccounts from "./pages/SettingsAccounts";
import SettingsPermissionReference from "./pages/SettingsPermissionReference";
import "./index.css";

// Firm workflow pages (docs/workflow-ui.md §5). Display-only — the backend
// re-checks every call. Keyed flows open to that flow's submitters and
// reviewers; the chosen-reviewer flows' list and detail pages need only a
// login, because the reviewer named on a request holds no key at all (the
// server scopes what each caller sees). Every New page needs the flow's own
// submit key ("new" and "amendment" are separate keys).
const keyedPerms = (flow) => [...submitKeysOf(flow), reviewKeyOf(flow)];

function AppContent() {
  const {
    isAuthenticated,
    loading: authLoading,
    mustChangePassword,
    clearMustChangePassword,
  } = useAuth();

  // Show loading spinner while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated()) {
    return <Login />;
  }

  // Blocking gate: seeded/reset accounts must set their own password first.
  if (mustChangePassword()) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ChangePassword forced onSuccess={clearMustChangePassword} />
      </div>
    );
  }

  return (
    <Layout>
      <Routes>
        {/* Dashboard Selection Page */}
        <Route path="/" element={<DashboardSelector />} />

        {/* My Records — the personal read-only surface (docs/modules/my-records.md).
            No RequirePermission by design: there is no key, and the server scopes
            every response to the caller (team membership / their staff row). */}
        <Route path="/my" element={<MyRecords />} />
        <Route path="/my/engagements/:id" element={<MyEngagementDetail />} />
        {/* The session page and the trainer's quiz editor: no permission key —
            the server decides by who you are to the session (my-records.md §6). */}
        <Route path="/my/sessions/:id" element={<MySessionDetail />} />
        <Route path="/my/sessions/:id/quiz" element={<MySessionQuizEditor />} />

        {/* Client Hub — read-only client lookup (docs/modules/client-hub.md).
            Gated by clients:hub:view (held by every account by default). */}
        <Route path="/clients" element={<RequirePermission permission="clients:hub:view"><ClientHub /></RequirePermission>} />
        <Route path="/clients/:id" element={<RequirePermission permission="clients:hub:view"><ClientHubEntity /></RequirePermission>} />

        {/* Dashboard modules (view-only) + their manage pages (docs/architecture.md §2).
            Manage/settings pages are wrapped in RequirePermission (display-only
            guard — the real enforcement is the backend middleware). */}
        <Route path="/qm" element={<RequirePermission permission="qm:view"><QMDashboard /></RequirePermission>} />
        <Route path="/qm/engagements" element={<RequirePermission permission="engagements:edit"><QMEngagements /></RequirePermission>} />
        <Route path="/qm/engagements/:id" element={<RequirePermission permission="engagements:edit"><QMEngagementDetail /></RequirePermission>} />
        <Route path="/qm/qc-records" element={<RequirePermission permission="qm:qc_records:edit"><QMQcRecords /></RequirePermission>} />
        {/* 建檔頁：與 workflow 的 New QC 同形狀的分步表單,必須排
            在 :id 之前,否則 'new' 會被當成記錄 id。 */}
        <Route path="/qm/qc-records/new" element={<RequirePermission permission="qm:qc_records:edit"><QMQcRecordNew /></RequirePermission>} />
        {/* 記錄詳情頁：有網址的頁面（不是 modal）,workflow
            詳情的「Open in Management →」因此連得到那一筆。 */}
        <Route path="/qm/qc-records/:id" element={<RequirePermission permission="qm:qc_records:edit"><QMQcRecordDetail /></RequirePermission>} />
        {/* CW/CC・Signed Financial Statements・QC35 的正式記錄＋狀態專屬區塊。
            都掛 engagements:edit——這些是 engagement 生命週期的資料。 */}
        {/* Screening Records — the formal layer behind the Screening flow
            (docs/modules/workflow-screening.md). 'new' must precede ':id'. */}
        <Route path="/qm/screening-records" element={<RequirePermission permission="qm:screening_records:edit"><QMScreeningRecords /></RequirePermission>} />
        <Route path="/qm/screening-records/new" element={<RequirePermission permission="qm:screening_records:edit"><QMScreeningRecordNew /></RequirePermission>} />
        <Route path="/qm/screening-records/:id" element={<RequirePermission permission="qm:screening_records:edit"><QMScreeningRecordDetail /></RequirePermission>} />
        <Route path="/qm/records/:kind" element={<RequirePermission permission="engagements:edit"><QMEngagementRecords /></RequirePermission>} />
        <Route path="/qm/records/:kind/:id" element={<RequirePermission permission="engagements:edit"><QMEngagementRecordDetail /></RequirePermission>} />
        <Route path="/qm/engagement-status" element={<Navigate to="/qm/engagements" replace />} />
        <Route path="/qm/staff" element={<RequirePermission permission="qm:staff:edit"><QMStaff /></RequirePermission>} />
        <Route path="/qm/monthly-records" element={<RequirePermission permission="qm:monthly:edit"><QMMonthlyRecords /></RequirePermission>} />
        <Route path="/qm/registers" element={<RequirePermission permission="qm:registers:edit"><QMRegisters /></RequirePermission>} />
        <Route path="/qm/adaptive-cookie" element={<RequirePermission permission="qm:timesheet_ops:edit"><QMAdaptiveCookie /></RequirePermission>} />
        <Route path="/qm/chargeable-import" element={<RequirePermission permission="qm:timesheet_ops:edit"><QMChargeableImport /></RequirePermission>} />
        <Route path="/qm/timesheet-check" element={<RequirePermission permission="qm:timesheet_ops:edit"><QMTimesheetCheck /></RequirePermission>} />

        {/* Professional Development (L&D) */}
        <Route path="/ld" element={<RequirePermission permission="ld:view"><LDDashboard /></RequirePermission>} />
        {/* One page, three screens — the group prop picks which tabs it shows
            (LDRecords.js TAB_GROUPS). */}
        <Route path="/ld/records" element={<RequirePermission permission="ld:training:edit"><LDRecords group="training" /></RequirePermission>} />
        <Route path="/ld/records/development" element={<RequirePermission permission="ld:development:edit"><LDRecords group="development" /></RequirePermission>} />
        <Route path="/ld/setup" element={<RequirePermission permission="ld:setup:edit"><LDRecords group="setup" /></RequirePermission>} />
        {/* Quiz Bank — the papers a session attaches as its post-course quiz
            (docs/modules/ld.md §5). 'new' must precede ':id'. */}
        <Route path="/ld/quizzes" element={<RequirePermission permission="ld:quiz:edit"><LDQuizzes /></RequirePermission>} />
        <Route path="/ld/reminders" element={<RequirePermission permission="ld:reminder:edit"><LDReminder /></RequirePermission>} />
        <Route path="/ld/quizzes/new" element={<RequirePermission permission="ld:quiz:edit"><LDQuizEditor /></RequirePermission>} />
        <Route path="/ld/quizzes/:id" element={<RequirePermission permission="ld:quiz:edit"><LDQuizEditor /></RequirePermission>} />

        {/* Workflow — firm process requests (docs/workflow-contract.md).
            Keyed flows open on the flow's submit key OR its review key;
            chosen-reviewer flows on a login (docs/workflow-ui.md §5). */}
        <Route path="/workflow" element={<WorkflowHub />} />
        {/* Keyed flows (review key) */}
        {[
          ['esc', '/workflow/esc', <WorkflowEsc />, <WorkflowEscNew />, <WorkflowEscDetail />],
        ].map(([flow, base, list, create, detail]) => (
          <Fragment key={base}>
            <Route path={base} element={<RequirePermission permission={keyedPerms(flow)}>{list}</RequirePermission>} />
            <Route path={`${base}/new`} element={<RequirePermission permission={submitKeysOf(flow)}>{create}</RequirePermission>} />
            <Route path={`${base}/:id`} element={<RequirePermission permission={keyedPerms(flow)}>{detail}</RequirePermission>} />
          </Fragment>
        ))}
        {/* Screening — its own submit key; list/detail also open for
            engagement submitters (a registration's members reach the
            branch request from the registration; the server scopes them). */}
        <Route path="/workflow/screening" element={<RequirePermission permission={[...submitKeysOf('screening'), reviewKeyOf('screening'), ...submitKeysOf('engreg')]}><WorkflowScreening /></RequirePermission>} />
        <Route path="/workflow/screening/new" element={<RequirePermission permission={submitKeysOf('screening')}><WorkflowScreeningNew /></RequirePermission>} />
        <Route path="/workflow/screening/:id" element={<RequirePermission permission={[...submitKeysOf('screening'), reviewKeyOf('screening'), ...submitKeysOf('engreg')]}><WorkflowScreeningDetail /></RequirePermission>} />
        {/* Chosen-reviewer flows: list and detail need a login only — the
            named reviewer holds no key. cwcc likewise: a covered engagement
            member without a key still opens their requests. */}
        {[
          ['client', '/workflow/client', <WorkflowClient />, <WorkflowClientNew />, <WorkflowClientDetail />],
          ['car', '/workflow/car', <WorkflowCar />, <WorkflowCarNew />, <WorkflowCarDetail />],
          ['engreg', '/workflow/engreg', <WorkflowEngReg />, <WorkflowEngRegNew />, <WorkflowEngRegDetail />, <WorkflowEngRegEdit />],
          ['ear', '/workflow/ear', <WorkflowEar />, <WorkflowEarNew />, <WorkflowEarDetail />],
          ['qc', '/workflow/qc', <WorkflowQc />, <WorkflowQcNew />, <WorkflowQcDetail />],
          ['qcad', '/workflow/qcad', <WorkflowQcad />, <WorkflowQcadNew />, <WorkflowQcadDetail />],
          ['cwcc', '/workflow/cwcc', <WorkflowCwcc />, <WorkflowCwccNew />, <WorkflowCwccDetail />],
          ['fs', '/workflow/fs', <WorkflowFs />, <WorkflowFsNew />, <WorkflowFsDetail />],
          ['qc35', '/workflow/qc35', <WorkflowQc35 />, <WorkflowQc35New />, <WorkflowQc35Detail />],
          ['qca', '/workflow/qca', <WorkflowQca />, <WorkflowQcaNew />, <WorkflowQcaDetail />],
          ['fsa', '/workflow/fsa', <WorkflowFsa />, <WorkflowFsaNew />, <WorkflowFsaDetail />],
          ['qc35a', '/workflow/qc35a', <WorkflowQc35a />, <WorkflowQc35aNew />, <WorkflowQc35aDetail />],
        ].map(([flow, base, list, create, detail, edit = null]) => (
          <Fragment key={base}>
            <Route path={base} element={list} />
            <Route path={`${base}/new`} element={<RequirePermission permission={submitKeysOf(flow)}>{create}</RequirePermission>} />
            {/* Modify is its own page, not a modal — same form, same keys as
                New (the server re-checks on PATCH). */}
            {edit && (
              <Route path={`${base}/:id/edit`} element={<RequirePermission permission={submitKeysOf(flow)}>{edit}</RequirePermission>} />
            )}
            <Route path={`${base}/:id`} element={detail} />
          </Fragment>
        ))}
        {/* A QC6 / QC7 record read from the workflow side — what an addendum's
            "QC Form Reference" opens. Login only; the server decides who may
            read the record (docs/modules/workflow-qcad.md §5). */}
        <Route path="/workflow/qcad/record/:id" element={<WorkflowQcadRecord />} />
        {/* PD declaration flows — gated on ld:submit OR the flow's review key
            (ld:submit is issued to every active
            user and revocable per person; docs/modules/ld-selfservice.md).
            QR check-in and feedback further down stay login-only. */}
        <Route path="/workflow/cpd" element={<RequirePermission permission={["ld:submit", "ld:cpd:review"]}><WorkflowCpd /></RequirePermission>} />
        <Route path="/workflow/cpd/new" element={<RequirePermission permission="ld:submit"><WorkflowCpdNew /></RequirePermission>} />
        <Route path="/workflow/cpd/:id" element={<RequirePermission permission={["ld:submit", "ld:cpd:review"]}><WorkflowCpdDetail /></RequirePermission>} />

        <Route path="/workflow/pq" element={<RequirePermission permission={["ld:submit", "ld:pq:review"]}><WorkflowPq /></RequirePermission>} />
        <Route path="/workflow/pq/new" element={<RequirePermission permission="ld:submit"><WorkflowPqNew /></RequirePermission>} />
        <Route path="/workflow/pq/:id" element={<RequirePermission permission={["ld:submit", "ld:pq:review"]}><WorkflowPqDetail /></RequirePermission>} />

        <Route path="/workflow/absence" element={<RequirePermission permission={["ld:submit", "ld:absence:review"]}><WorkflowAbsence /></RequirePermission>} />
        <Route path="/workflow/absence/new" element={<RequirePermission permission="ld:submit"><WorkflowAbsenceNew /></RequirePermission>} />
        <Route path="/workflow/absence/:id" element={<RequirePermission permission={["ld:submit", "ld:absence:review"]}><WorkflowAbsenceDetail /></RequirePermission>} />

        <Route path="/workflow/dispute" element={<RequirePermission permission={["ld:submit", "ld:absence:review"]}><WorkflowDispute /></RequirePermission>} />
        <Route path="/workflow/dispute/new" element={<RequirePermission permission="ld:submit"><WorkflowDisputeNew /></RequirePermission>} />
        <Route path="/workflow/dispute/:id" element={<RequirePermission permission={["ld:submit", "ld:absence:review"]}><WorkflowDisputeDetail /></RequirePermission>} />

        {/* L&D self-service — QR check-in / feedback, login only
            (docs/modules/ld-selfservice.md) */}
        <Route path="/checkin/:token" element={<CheckinPage />} />
        <Route path="/feedback/:sessionId" element={<FeedbackForm />} />
        <Route path="/quiz/:sessionId" element={<QuizPage />} />

        {/* Management hub + Settings (master data + system) */}
        <Route path="/manage" element={<ManageData />} />
        <Route path="/settings/clients" element={<RequirePermission permission="settings:clients:edit"><SettingsClients /></RequirePermission>} />
        <Route path="/settings/lists" element={<RequirePermission permission="settings:lists:edit"><SettingsLists /></RequirePermission>} />
        <Route path="/settings/overdue" element={<RequirePermission permission="settings:overdue:edit"><SettingsOverdue /></RequirePermission>} />
        {/* Training Settings moved into L&D Setup as a tab — old links and bookmarks land there. */}
        <Route path="/settings/ld" element={<Navigate to="/ld/setup?tab=trainingSettings" replace />} />
        <Route path="/settings/roles" element={<RequirePermission permission="admin:users"><SettingsRoles /></RequirePermission>} />
        <Route path="/settings/accounts" element={<RequirePermission permission="admin:users"><SettingsAccounts /></RequirePermission>} />
        <Route path="/settings/notifications" element={<RequirePermission permission="settings:notifications:edit"><SettingsNotifications /></RequirePermission>} />
        <Route path="/settings/admin" element={<RequirePermission permission="admin:settings"><SettingsAdmin /></RequirePermission>} />
        <Route path="/settings/legacy-import" element={<Navigate to="/settings/admin?tab=legacy-import" replace />} />
        <Route path="/settings/permission-reference" element={<RequirePermission permission="admin:users"><SettingsPermissionReference /></RequirePermission>} />
        <Route path="/settings/activity-log" element={<Navigate to="/settings/admin" replace />} />
        {/* Coverage merged into the reference page — holders are a column there now. */}
        <Route path="/settings/permission-coverage" element={<Navigate to="/settings/permission-reference" replace />} />

        {/* Old admin path → Accounts */}
        <Route path="/admin/*" element={<Navigate to="/settings/accounts" replace />} />

        {/* Fallback - redirect to dashboard selection */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <UnifiedDashboardProvider>
          <AppContent />
        </UnifiedDashboardProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
